import moment from "moment";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
  VisitDataChart,
  VisitDataRow,
} from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import {
  formatDateToUTCWithFormat,
  parseMMDDYYYY,
} from "../../../utils/formatDate";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

/**
 * Checks if a medical PA order has potential to exhaust its authorized visits.
 *
 * Evaluates whether the remaining visits for any J-code with "Auth on File"
 * status will be exhausted based on scheduled visits between the order
 * creation date and the date of service.
 *
 * @param generalOrderDetails - General order info (payload dates, timestamps)
 * @param authOnFileDetails - Auth-on-file data containing J-codes and visit charts
 * @returns `true` if any J-code's visits will potentially exhaust, `false` otherwise
 */
export const checkIfPotentialExhaustVisits = (
  generalOrderDetails: GeneralOrderDetails,
  authOnFileDetails: AuthOnFile,
) => {
  // Get all J-codes from the order
  const jCodes = getAllJCodesFromAuthOnFile(authOnFileDetails);
  if (jCodes === undefined || !jCodes?.length) {
    return false;
  }

  // Filter for J-codes with "Auth on File" status
  const jCodesWithAuthOnFileStatus = getJCodesWithAuthOnFileStatus(
    jCodes,
  ).filter((jCode) => jCode?.num_visits !== 999);
  if (!jCodesWithAuthOnFileStatus?.length) {
    return false;
  }

  const visitDataCharts = authOnFileDetails?.visit_data_charts;

  return jCodesWithAuthOnFileStatus.some((jCode) =>
    checkIfVisitsWillExhaust(
      jCode,
      visitDataCharts,
      generalOrderDetails?.payload?.date_of_service ?? "",
      generalOrderDetails?.timestamps?.order_created_at ?? "",
    ),
  );
};

/**
 * Filters J-codes to only include those with "Auth on File" status.
 *
 * @param jCodes - Array of J-code details to filter
 * @returns Array of J-codes that have the "Auth on File" authorization status
 */
const getJCodesWithAuthOnFileStatus = (jCodes: JCodeDetails[]) => {
  return jCodes.filter(
    (jCode) =>
      jCode && jCode.auth_status === MedicalPaOrdersAuthStatus.AuthOnFile,
  );
};

/**
 * Determines if the scheduled visits for a J-code will exhaust the remaining authorized visits.
 *
 * Compares the number of visits scheduled between the order creation date and
 * date of service against the remaining visits for the J-code authorization.
 *
 * @param jCodesWithAuthOnFileStatus - The J-code details with "Auth on File" status
 * @param visitDataCharts - Visit chart array from auth-on-file
 * @param dateOfService - The scheduled date of service (MM/DD/YYYY format)
 * @param createdAt - The order creation date (MM/DD/YYYY format)
 * @returns `true` if scheduled visits will exhaust or exceed remaining visits, `false` otherwise
 */
const checkIfVisitsWillExhaust = (
  jCodesWithAuthOnFileStatus: JCodeDetails,
  visitDataCharts: VisitDataChart[] | undefined,
  dateOfService: string,
  createdAt: string,
) => {
  // Validate dates are present and valid before proceeding
  if (
    !createdAt ||
    !dateOfService ||
    !moment(createdAt).isValid() ||
    !moment(dateOfService).isValid()
  ) {
    return false;
  }

  const visitDataChartDetail = getVisitDataChartByDescription(
    visitDataCharts,
    jCodesWithAuthOnFileStatus?.medicine_name ??
      jCodesWithAuthOnFileStatus?.description ??
      "",
  );
  if (visitDataChartDetail && visitDataChartDetail.totalRows > 0) {
    const allDataPoints = visitDataChartDetail.data;
    // Format createdAt/dateOfService to MM/DD/YYYY in UTC so the calendar day
    // matches the API's UTC-midnight ISO strings regardless of viewer timezone.
    const createdAtFormatted = formatDateToUTCWithFormat(
      createdAt,
      "MM/DD/YYYY",
    );
    const dateOfServiceFormatted = formatDateToUTCWithFormat(
      dateOfService,
      "MM/DD/YYYY",
    );
    const numberOfVisits = getTheNumberOfVisitsBetweenCreatedAtAndDateOfService(
      createdAtFormatted,
      dateOfServiceFormatted,
      allDataPoints,
    );
    if (numberOfVisits >= (jCodesWithAuthOnFileStatus?.visits_remaining ?? 0)) {
      return true;
    }
  }
  return false;
};

/**
 * Resolves visit chart rows for a J-code description from the visit-data
 * chart array on auth-on-file.
 *
 * @param visitDataCharts - Visit chart array from auth-on-file
 * @param jCodeDescription - J-code description used to match chart data
 * @returns Normalized chart rows with row count when a match is found
 */
const getVisitDataChartByDescription = (
  visitDataCharts: VisitDataChart[] | undefined,
  jCodeDescription: string,
): { data: VisitDataRow[]; totalRows: number } | undefined => {
  if (!visitDataCharts?.length || !jCodeDescription) {
    return undefined;
  }

  const normalizedDescription = jCodeDescription.trim().toLowerCase();
  const chart = visitDataCharts.find(
    (visitDataChart: VisitDataChart) =>
      (visitDataChart?.medicine_name ?? "").trim().toLowerCase() ===
        normalizedDescription ||
      (visitDataChart?.medicine_name ?? "")
        .trim()
        .toLowerCase()
        .includes(normalizedDescription),
  );

  const chartRows = chart?.data?.data ?? [];
  const totalRows = chart?.total_rows ?? chart?.data?.total_rows ?? 0;
  return chart ? { data: chartRows, totalRows } : undefined;
};

/**
 * Counts the number of visits that fall between the order creation date and date of service.
 *
 * Filters visit data to only include visits where the date is strictly after
 * the creation date and strictly before the date of service.
 *
 * @param createdAt - The order creation date (MM/DD/YYYY format)
 * @param dateOfService - The scheduled date of service (MM/DD/YYYY format)
 * @param visitData - Visit chart rows containing visit dates
 * @returns The count of visits that fall within the specified date range (exclusive)
 */
const getTheNumberOfVisitsBetweenCreatedAtAndDateOfService = (
  createdAt: string,
  dateOfService: string,
  visitData: VisitDataRow[],
) => {
  // Early return if dates are empty or invalid
  if (!createdAt || !dateOfService) {
    return 0;
  }

  const createdAtDate = parseMMDDYYYY(createdAt);
  const dateOfServiceDate = parseMMDDYYYY(dateOfService);

  // Validate parsed dates are not Invalid Date
  if (isNaN(createdAtDate.getTime()) || isNaN(dateOfServiceDate.getTime())) {
    return 0;
  }

  return visitData.filter((data) => {
    if (!data.Date) return false;
    const dataDate = parseMMDDYYYY(data.Date);
    if (isNaN(dataDate.getTime())) return false;
    return (
      dataDate.getTime() >= createdAtDate.getTime() &&
      dataDate.getTime() <= dateOfServiceDate.getTime()
    );
  }).length;
};
